// Docs: https://www.instantdb.com/docs/modeling-data

import {i} from "@instantdb/react";

const _schema = i.schema({
  // We inferred 4 attributes!
  // Take a look at this schema, and if everything looks good,
  // run `push schema` again to enforce the types.
  entities: {
    $files: i.entity({path: i.string().unique().indexed(), url: i.string()}),
    $users: i.entity({email: i.string().unique().indexed().optional()}),
    conversations: i.entity({
      conversationId: i.string().unique().indexed(),
      createdAt: i.date(),
      title: i.string(),
      type: i.string().optional(),
      updatedAt: i.date(),
    }),
    integrations: i.entity({
      createdAt: i.date(),
      details: i.any(),
      hasMCP: i.boolean(),
      hasVault: i.boolean(),
      profileKey: i.string().unique(),
      serverUrl: i.string().optional(),
      slug: i.string(),
      toolsSchema: i.json().optional(),
      updatedAt: i.date(),
    }),
    profiles: i.entity({
      createdAt: i.date(),
      email: i.string(),
      key: i.string().unique(),
      name: i.string().optional(),
      role: i.string(),
      status: i.boolean(),
      updatedAt: i.date().optional(),
    }),
    shortcuts: i.entity({
      compiled: i.json().optional(),
      createdAt: i.date(),
      description: i.string().optional(),
      icon: i.string(),
      isActive: i.boolean().optional(),
      name: i.string(),
      slug: i.string().unique(),
      updatedAt: i.date(),
      version: i.number().optional(),
      workflow: i.json().optional(),
      workflowDag: i.json().optional(),
    }),
    agents: i.entity({
      name: i.string(),
      description: i.string().optional(),
      icon: i.string().optional(),
      slug: i.string().unique(),
      isActive: i.boolean(),
      createdAt: i.date(),
      updatedAt: i.date(),
      config: i.json().optional(), // Dynamic agent configuration
    }),
    vault: i.entity({
      createdAt: i.date().optional(),
      key: i.string().unique(),
      updatedAt: i.date().optional(),
      value: i.string(),
    }),
    waitlist: i.entity({
      createdAt: i.date(),
      email: i.string().unique().indexed(),
      name: i.string().optional(),
      updatedAt: i.date().optional(),
    }),
    workspaces: i.entity({
      createdAt: i.date(),
      name: i.string(),
      slug: i.string().unique().indexed(),
      status: i.boolean(),
      updatedAt: i.date().optional(),
    }),
    whatsappChats: i.entity({
      jid: i.string().unique().indexed(),
      name: i.string().optional(),
      lastMessageTime: i.date().optional(),
      isGroup: i.boolean(),
      unreadCount: i.number().optional(),
      createdAt: i.date(),
      updatedAt: i.date(),
    }),
    whatsappMessages: i.entity({
      messageId: i.string().unique().indexed(),
      chatJid: i.string().indexed(),
      sender: i.string().optional(),
      content: i.string(),
      timestamp: i.date().indexed(), // Index for chronological ordering
      isFromMe: i.boolean().indexed(), // Index for filtering by sender
      messageType: i.string().optional(),
      createdAt: i.date().indexed(), // Index for creation ordering
    }),
  },
  links: {
    conversations$profile: {
      forward: {on: "conversations", has: "one", label: "$profile"},
      reverse: {on: "profiles", has: "many", label: "conversations"},
    },
    conversations$shortcut: {
      forward: {on: "conversations", has: "one", label: "$shortcut"},
      reverse: {on: "shortcuts", has: "many", label: "conversations"},
    },
    integrations$profile: {
      forward: {on: "integrations", has: "one", label: "$profile"},
      reverse: {on: "profiles", has: "many", label: "integrations"},
    },
    integrationsShortcuts: {
      forward: {on: "integrations", has: "many", label: "shortcuts"},
      reverse: {on: "shortcuts", has: "many", label: "integrations"},
    },
    integrationsAgents: {
      forward: {on: "integrations", has: "many", label: "agents"},
      reverse: {on: "agents", has: "many", label: "integrations"},
    },
    profiles$avatar: {
      forward: {on: "profiles", has: "one", label: "$avatar"},
      reverse: {on: "$files", has: "one", label: "$profileAvatar"},
    },
    profiles$user: {
      forward: {on: "profiles", has: "one", label: "$user"},
      reverse: {on: "$users", has: "many", label: "profiles"},
    },
    profiles$workspace: {
      forward: {on: "profiles", has: "one", label: "$workspace"},
      reverse: {on: "workspaces", has: "many", label: "profiles"},
    },
    shortcuts$profile: {
      forward: {on: "shortcuts", has: "one", label: "$profile"},
      reverse: {on: "profiles", has: "many", label: "shortcuts"},
    },
    agents$profile: {
      forward: {on: "agents", has: "one", label: "$profile"},
      reverse: {on: "profiles", has: "many", label: "agents"},
    },
    agents$workspace: {
      forward: {on: "agents", has: "one", label: "$workspace"},
      reverse: {on: "workspaces", has: "many", label: "agents"},
    },
    shortcuts$workspace: {
      forward: {on: "shortcuts", has: "one", label: "$workspace"},
      reverse: {on: "workspaces", has: "many", label: "shortcuts"},
    },
    workspaces$logo: {
      forward: {on: "workspaces", has: "one", label: "$logo"},
      reverse: {on: "$files", has: "one", label: "$workspaceLogo"},
    },
    workspaces$user: {
      forward: {on: "workspaces", has: "one", label: "$user"},
      reverse: {on: "$users", has: "many", label: "workspaces"},
    },
    whatsappChatsMessages: {
      forward: {on: "whatsappChats", has: "many", label: "whatsappMessages"},
      reverse: {on: "whatsappMessages", has: "one", label: "$whatsappChat"},
    },
  },
  rooms: {},
});

// This helps Typescript display nicer intellisense
type _AppSchema = typeof _schema;
interface AppSchema extends _AppSchema {}
const schema: AppSchema = _schema;

export type {AppSchema};
export default schema;
